import React, { createContext, ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Api from "@/store/api.ts";
import { EventBus, EventBusEvents } from "@/store/event-bus.ts";
import { isPlatform, platformMostAI, platformWizyChat } from "@/domains.ts";

export type UserTheme = "light" | "dark" | "system";
export type Theme = "light" | "dark";

interface UserProfile {
  name: string;
  surname: string;
  avatar: string;
}

interface BackendSettings {
  email: string | null;
  name: string | null;
  avatar_url: string | null;
  userID: string | null;
  usage: number | null;
  usageLimit: number | null;
  subscription: any | null;
  messageCredits: number | null;
  profile: UserProfile | null;
}

interface UserSettings extends BackendSettings {
  userTheme: UserTheme;
  theme: Theme;
  model: string | null;
  instructions: string;
  speechModel: string | null;
  speechVoice: string | null;
  speechSpeed: number | null;
}

const defaultUserSettings: UserSettings = {
  userTheme: "system",
  theme: "light",
  model: null,
  instructions: "",
  speechModel: "tts-1",
  speechVoice: "echo",
  speechSpeed: 1.0,

  email: null,
  userID: null,
  usage: null,
  usageLimit: null,
  subscription: null,
  messageCredits: null,
};

const determineEffectiveTheme = (userTheme: UserTheme): Theme => {
  if (userTheme === "system" || !userTheme) {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return userTheme;
};

export interface AnonRoute {
  regexp: RegExp;
  platform: string;
}

export const anonRoutes: AnonRoute[] = [
  {
    platform: platformMostAI,
    regexp: /^\/chat/,
  },
  {
    platform: platformMostAI,
    regexp: /^\/studio/,
  },
  {
    platform: platformMostAI,
    regexp: /^\/dashboard/,
  },
  {
    platform: "",
    regexp: /^\/login/,
  },
];

export const UserContext = createContext<{
  userSettings: UserSettings;
  setUserSettings: React.Dispatch<React.SetStateAction<UserSettings>>;
  invalidateSettings: () => void;
}>({
  userSettings: defaultUserSettings,
  setUserSettings: () => {},
  invalidateSettings: () => {},
});

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider = ({ children }: UserProviderProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [userSettings, setUserSettings] = useState<UserSettings>(() => {
    const storedUserTheme = localStorage.getItem("theme");
    const userTheme: UserTheme =
      storedUserTheme === "light" ||
      storedUserTheme === "dark" ||
      storedUserTheme === "system"
        ? storedUserTheme
        : defaultUserSettings.userTheme;

    const model =
      localStorage.getItem("defaultModel") || defaultUserSettings.model;
    const instructions =
      localStorage.getItem("defaultInstructions") ||
      defaultUserSettings.instructions;
    const speechModel =
      localStorage.getItem("defaultSpeechModel") ||
      defaultUserSettings.speechModel;
    const speechVoice =
      localStorage.getItem("defaultSpeechVoice") ||
      defaultUserSettings.speechVoice;

    const speechSpeedRaw = localStorage.getItem("defaultSpeechSpeed");
    const speechSpeed =
      speechSpeedRaw !== null
        ? Number(speechSpeedRaw)
        : defaultUserSettings.speechSpeed;

    const effectiveTheme = determineEffectiveTheme(userTheme);

    let res = {
      userTheme: userTheme,
      theme: effectiveTheme,
      model,
      instructions,
      speechModel,
      speechVoice,
      speechSpeed,

      email: null,
      userID: null,
      usage: null,
      usageLimit: null,
      subscription: null,
      messageCredits: null,
    };

    return res;
  });

  const anonRouteCheck = () => {
    if (userSettings.email) {
      return;
    }

    let foundAnonRoute = anonRoutes.find((item: AnonRoute) => {
      if (item.platform != "" && !isPlatform(item.platform)) {
        return false;
      }

      return !!item.regexp.test(pathname);
    });

    if (!foundAnonRoute) {
      navigate("/login");
    }
  };

  const invalidateSettings = () => {
    Api.getSettings().then((settings) => {
      if (!settings) {
        anonRouteCheck();
      }
      if (settings) {
        if (pathname == "/login") {
          navigate("/dashboard");
        }

        EventBus.getInstance().dispatch(
          EventBusEvents.settingsUpdated,
          settings,
        );

        let settingsMap = {
          email: settings.email,
          name: settings.name,
          avatar_url: settings.avatar_url,
          userID: settings.user_id,
          usage: settings.usage,
          usageLimit: settings.usage_limit,
          subscription: settings.subscription,
          messageCredits: settings.message_credits,
        };

        localStorage.setItem(
          "user_settings_cache",
          JSON.stringify(settingsMap),
        );

        setUserSettings({ ...userSettings, ...settingsMap });
      }
    });
  };

  useEffect(invalidateSettings, []);

  // SETTINGS FROM CHATS TODO: REFACTOR THEM ------------------------
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    mediaQuery.addEventListener("change", mediaQueryChangeHandler);
    updateTheme();

    return () => {
      mediaQuery.removeEventListener("change", mediaQueryChangeHandler);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("theme", userSettings.userTheme);
  }, [userSettings.userTheme]);

  useEffect(() => {
    if (userSettings.model === null || userSettings.model === "") {
      localStorage.removeItem("defaultModel");
    } else {
      localStorage.setItem("defaultModel", userSettings.model);
    }
  }, [userSettings.model]);

  useEffect(() => {
    if (userSettings.instructions === "") {
      localStorage.removeItem("defaultInstructions");
    } else {
      localStorage.setItem("defaultInstructions", userSettings.instructions);
    }
  }, [userSettings.instructions]);

  useEffect(() => {
    const newEffectiveTheme = determineEffectiveTheme(userSettings.userTheme);
    setUserSettings((prevSettings) => ({
      ...prevSettings,
      theme: newEffectiveTheme,
    }));

    if (newEffectiveTheme === "dark") {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  }, [userSettings.userTheme]);

  const mediaQueryChangeHandler = (e: MediaQueryListEvent) => {
    const newSystemTheme: Theme = e.matches ? "dark" : "light";
    if (userSettings.userTheme === "system") {
      setUserSettings((prevSettings) => ({
        ...prevSettings,
        theme: newSystemTheme,
      }));
    }
  };

  const updateTheme = () => {
    const newEffectiveTheme = determineEffectiveTheme(
      userSettings.userTheme || "system",
    );
    if (newEffectiveTheme !== userSettings.theme) {
      setUserSettings((prevSettings) => ({
        ...prevSettings,
        theme: newEffectiveTheme,
      }));
    }
    if (newEffectiveTheme === "dark") {
      document.body.classList.add("dark");
    } else {
      document.body.classList.remove("dark");
    }
  };

  useEffect(() => {
    if (userSettings.speechModel === null || userSettings.speechModel === "") {
      localStorage.removeItem("defaultSpeechModel");
    } else {
      localStorage.setItem("defaultSpeechModel", userSettings.speechModel);
    }
  }, [userSettings.speechModel]);

  useEffect(() => {
    if (userSettings.speechVoice === null || userSettings.speechVoice === "") {
      localStorage.removeItem("defaultSpeechVoice");
    } else {
      localStorage.setItem("defaultSpeechVoice", userSettings.speechVoice);
    }
  }, [userSettings.speechVoice]);

  useEffect(() => {
    if (
      userSettings.speechSpeed === null ||
      userSettings.speechSpeed === undefined ||
      userSettings.speechSpeed < 0.25 ||
      userSettings.speechSpeed > 4.0
    ) {
      localStorage.removeItem("defaultSpeechSpeed");
    } else {
      localStorage.setItem(
        "defaultSpeechSpeed",
        String(userSettings.speechSpeed),
      );
    }
  }, [userSettings.speechSpeed]);

  return (
    <UserContext.Provider
      value={{ userSettings, setUserSettings, invalidateSettings }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Usage hint
// const { userSettings, setUserSettings } = useContext(UserContext);
